import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { updateUploadedFileData } from 'src/app/data/store/actions/uploading-files.actions';
import { IUploadingFile } from 'src/app/shared/services/posts/posts.type';
import { PostsService } from 'src/app/shared/services/posts/posts.service';
import { imagePlaceholderUrl } from 'src/app/shared/constants';
import { UtilsService } from 'src/app/shared/services/utils.service';

import { IonicModule } from '@ionic/angular';
import { AddBaseUrlPipe } from '../../../../../../../../shared/pipe/add-baseUrl/add-base-url.pipe';

const filesIntervalsMs = [1000, 2000, 4000, 8000] as const;

@Component({
  selector: 'app-photo-report-image-item',
  templateUrl: './photo-report-image-item.component.html',
  styleUrls: ['./photo-report-image-item.component.scss'],
  imports: [IonicModule, AddBaseUrlPipe],
})
export class PhotoReportImageItemComponent implements OnInit, OnDestroy, OnChanges {
  private _store = inject(Store);
  private _postsService = inject(PostsService);
  private _utils = inject(UtilsService);

  @Input() image!: IUploadingFile;

  @Input() categoryName!: string;

  @Input() isLoading = false;

  @Output() rotate = new EventEmitter<{ image: IUploadingFile; categoryName: string }>();

  @Output() delete = new EventEmitter<{ localId: number; id?: number; categoryName: string }>();

  @Output() loaded = new EventEmitter<{ localId: number; id?: number; categoryName: string }>();

  private _isEditMode = false;

  private _refreshSubscription?: Subscription;

  private _loadErrorHandled = false;

  private _pollAttempt = 0;

  readonly imagePlaceholderUrl = imagePlaceholderUrl;

  ngOnInit(): void {
    if (this.image.id && !this.image.uploadedFileData) {
      this._isEditMode = true;
      // @ts-expect-error неверно типизирована переменная image
      this.image = { ...this.image, uploadedFileData: this.image };
    } else {
      if (this._handleErrorLoadedIfNeeded()) {
        return;
      }
      if (this.image.uploadedFileData && this.image.uploadedFileData.loaded === true) {
        this.loaded.emit({
          localId: this.image.localId,
          id: this.image.id,
          categoryName: this.categoryName,
        });
      }
      this._startRefreshingIfNeeded();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this._isEditMode) {
      // @ts-expect-error неверно типизирована переменная image
      this.image = { ...this.image, uploadedFileData: this.image };
    }
    if (!this._isEditMode && changes['image'] && !changes['image'].firstChange) {
      if (this._handleErrorLoadedIfNeeded()) {
        this._stopRefreshing();
        return;
      }
      if (
        this.image.uploadedFileData &&
        this.image.uploadedFileData.loaded === true &&
        changes['image'].previousValue?.uploadedFileData?.loaded !== true
      ) {
        this.loaded.emit({
          localId: this.image.localId,
          id: this.image.id,
          categoryName: this.categoryName,
        });
        this._stopRefreshing();
        return;
      }
      if (!this._refreshSubscription) {
        this._startRefreshingIfNeeded();
      }
    }
  }

  ngOnDestroy(): void {
    this._stopRefreshing();
  }

  public onRotateClick(): void {
    this.rotate.emit({ image: this.image, categoryName: this.categoryName });
  }

  public onDeleteClick(): void {
    this.delete.emit({
      localId: this.image.localId,
      id: this.image.id,
      categoryName: this.categoryName,
    });
  }

  public isImageLoading(): boolean {
    if (this._loadErrorHandled || this.image.uploadedFileData?.errorLoaded) {
      return false;
    }
    return (
      this.image.isLoading || !this.image.uploadedFileData || !this.image.uploadedFileData.loaded
    );
  }

  public getImageSrc(): string {
    return this.image.isLoading ||
      !this.image.uploadedFileData ||
      !this.image.uploadedFileData.loaded
      ? this.imagePlaceholderUrl
      : this.image.uploadedFileData.path;
  }

  public getRotateAngle(): number {
    return this.image.localRotateAngle === 0 || this.image.localRotateAngle
      ? this.image.localRotateAngle
      : this.image.rotateAngle || 0;
  }

  private _handleErrorLoadedIfNeeded(): boolean {
    if (!this.image.uploadedFileData?.errorLoaded) {
      return false;
    }
    this._emitLoadError();
    return true;
  }

  private _emitLoadError(): void {
    if (this._loadErrorHandled) {
      return;
    }
    this._loadErrorHandled = true;
    this._stopRefreshing();
    this._utils.openNotification('Ошибка загрузки', true);
    this.delete.emit({
      localId: this.image.localId,
      id: this.image.id,
      categoryName: this.categoryName,
    });
  }

  private _startRefreshingIfNeeded(): void {
    if (
      this._refreshSubscription ||
      !this.image.id ||
      !this.image.uploadedFileData ||
      this.image.uploadedFileData.loaded === true ||
      this.image.uploadedFileData.errorLoaded ||
      this._loadErrorHandled
    ) {
      return;
    }

    this._pollAttempt = 0;
    this._scheduleNextPoll();
  }

  private _scheduleNextPoll(): void {
    const fileId = this.image.id;
    if (!fileId) {
      this._stopRefreshing();
      return;
    }

    const delay = filesIntervalsMs[Math.min(this._pollAttempt, filesIntervalsMs.length - 1)];
    this._pollAttempt += 1;

    this._refreshSubscription = timer(delay)
      .pipe(switchMap(() => this._postsService.getFile(fileId)))
      .subscribe({
        next: (fileData) => {
          if (!fileData) {
            this._scheduleNextPoll();
            return;
          }

          this._store.dispatch(
            updateUploadedFileData({
              localId: this.image.localId,
              imageCategory: this.categoryName,
              uploadedFileData: { ...fileData, path: `${fileData.path}?${Date.now()}` },
            }),
          );

          if (fileData.errorLoaded) {
            this._emitLoadError();
            return;
          }

          if (fileData.loaded) {
            this.loaded.emit({
              localId: this.image.localId,
              id: fileId,
              categoryName: this.categoryName,
            });
            this._stopRefreshing();
            return;
          }

          this._scheduleNextPoll();
        },
        error: () => {
          this._loadErrorHandled = true;
          this._stopRefreshing();
        },
      });
  }

  private _stopRefreshing(): void {
    if (this._refreshSubscription) {
      this._refreshSubscription.unsubscribe();
      this._refreshSubscription = undefined;
    }
  }
}
